import Typography from '@mui/material/Typography';
import type {TypographyProps} from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import React from 'react';
import type {ElementType} from 'react';

import {extractSystemStyles, type WithSystemProps} from '@famly/mf_system_system-props';
import {useDataProps} from '@famly/mf_ts-utils_data-aria-props';
import {getEmphasizedFontWeight} from '@famly/mf_theme';

export type TextProps = {
    children: TypographyProps['children'];
    ellipsis?: boolean;
    emphasized?: boolean;
    whiteSpace?: TypographyProps['whiteSpace'];
    variant: TypographyProps['variant'];
    id?: string;

    /**
     * When set, the text will be displayed with a maximum number of lines.
     * If the text exceeds the number of lines, it will be truncated and an ellipsis will be displayed.
     */
    lines?: number;

    /**
     * When set, the text will be displayed as a number.
     * Note that this will not apply any formatting — only the font itself and some font-attributes will be changed.
     */
    numeric?: boolean;
    component?: 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

    /**
     * Trims the line box down to the cap-height→baseline band, so the box the text occupies holds
     * only the glyphs the eye actually reads.
     *
     * Use it when the text sits next to an icon in a centred row. Normally the line box also
     * reserves the font's leading, ascender overshoot and descender depth, so a word containing a
     * descender ("Date ran**g**e") is centred differently from one without ("Status") and reads as
     * sitting lower than the icon. Trimming makes the alignment depend on the cap band alone, which
     * is identical for every string and font.
     *
     * Descenders still render — they are allowed to overflow the trimmed box — so this stays safe
     * to combine with `ellipsis`.
     */
    trimToCapHeight?: boolean;
};

/**
 * Vertical room kept around the trimmed cap→baseline band for the glyph parts that fall outside it
 * (descenders, ascender overshoot, diacritics). `ellipsis` sets `overflow: hidden`, which clips to
 * the padding box, so without this the trim would cut the tail off a "g". The matching negative
 * margin takes the room back out of the layout box, leaving the cap band as what gets centred.
 */
const CAP_HEIGHT_TRIM_ALLOWANCE = '0.4em';

const defaultComponent = (variant: TypographyProps['variant']): ElementType<any> => {
    switch (variant) {
        case 'h1':
            return 'h1';
        case 'h2':
            return 'h2';
        case 'h3':
            return 'h3';
        case 'h4':
            return 'h4';
        case 'h5':
            return 'h5';
        case 'h6':
            return 'h6';
        case 'body':
            return 'p';
        case 'body-small':
            return 'p';
        case 'subtitle':
            return 'p';
        case 'micro':
            return 'p';
        default:
            return 'p';
    }
};

export const Text = (props: WithSystemProps<TextProps>) => {
    const {
        variant,
        children,
        emphasized,
        ellipsis,
        numeric,
        lines,
        component: componentFromProp,
        id,
        trimToCapHeight,
        ...rest
    } = props;
    const component = componentFromProp ?? defaultComponent(variant);
    const dataProps = useDataProps(rest);
    const {sx: systemSx} = extractSystemStyles(rest);

    // Ensure we always have a valid variant for MUI Typography
    const validVariant = variant || 'body';

    const sx = React.useCallback<Extract<SxProps<Theme>, Function>>(
        theme => {
            return {
                ...systemSx(theme),
                fontWeight: emphasized ? getEmphasizedFontWeight(validVariant) : undefined,
                ...getNumericStyle(numeric, validVariant),
                ...(!ellipsis &&
                    lines && {
                        display: '-webkit-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: lines,
                    }),
                ...(trimToCapHeight && {
                    textBoxTrim: 'trim-both',
                    textBoxEdge: 'cap alphabetic',
                    paddingBlock: CAP_HEIGHT_TRIM_ALLOWANCE,
                    marginBlock: `-${CAP_HEIGHT_TRIM_ALLOWANCE}`,
                }),
            };
        },
        [systemSx, emphasized, validVariant, numeric, ellipsis, lines, trimToCapHeight],
    );

    return (
        <Typography component={component} sx={sx} variant={validVariant} noWrap={ellipsis} {...dataProps} id={id}>
            {children}
        </Typography>
    );
};

const getNumericStyle = (isNumeric?: boolean, variant?: TypographyProps['variant']) => {
    if (!isNumeric) {
        return {};
    }

    // For subtitle and micro, keep their Inter font but add numeric features
    if (variant === 'subtitle' || variant === 'micro') {
        return {
            fontVariantNumeric: 'lining-nums tabular-nums stacked-fractions slashed-zero',
            fontFeatureSettings: "'ss02' on, 'ss04' on, 'cv11' on, 'ss01' on, 'cv04' on",
        };
    }

    // For other variants (including undefined), switch to Inter variable font and add numeric features
    return {
        fontFamily: 'Inter',
        fontVariantNumeric: 'lining-nums tabular-nums stacked-fractions slashed-zero',
        fontFeatureSettings: "'ss02' on, 'ss04' on, 'cv11' on, 'ss01' on, 'cv04' on",
        fontVariationSettings: "'wght' 400",
    };
};
